import Markdown from 'markdown-to-jsx'
import Link from 'next/link'
import { FC, useMemo } from 'react'

import { sharedTypography } from '../../utils/stitchesShared-utils'
import { css, styled } from '../stitches.config'

type MarkdownToJsxProps = { markdown: string; baseUrl?: string }

export const MarkdownToJsx: FC<MarkdownToJsxProps> = ({
  markdown,
  baseUrl, // Optional. If set, relative URLs in markdown will be prefixed with this base domain. This prevents broken or incorrect links in the CMS, where relative URLs would otherwise not point to the intended customer site page.
}) => {
  const overrides = useMemo(() => {
    const LinkWithBase = props => {
      const href: string = props.href || ''
      const isAbsolute = /^https?:\/\//i.test(href) || href.startsWith('mailto:') || href.startsWith('tel:')
      const isHash = href.startsWith('#')
      const normalizedHref =
        !isAbsolute && !isHash && baseUrl ? `${baseUrl.replace(/\/$/, '')}/${href.replace(/^\//, '')}` : href
      const nextProps = { ...props, href: normalizedHref }
      if (/^https?:\/\//i.test(normalizedHref)) {
        return <A {...nextProps} target="_blank" rel="noopener noreferrer" />
      }
      return <CustomLink {...nextProps} prefetch={true} />
    }

    return {
      h1: H1,
      h2: H2,
      h3: H3,
      h4: H4,
      h5: H5,
      h6: H6,
      p: P,
      blockquote: Blockquote,
      ol: OL,
      ul: UL,
      li: LI,
      a: LinkWithBase,
      span: Span,
      pre: Pre,
    }
  }, [baseUrl])
  return (
    <MarkdownStyled
      options={{
        forceBlock: true,
        overrides: overrides,
      }}>
      {markdown}
    </MarkdownStyled>
  )
}

const Pre = styled('pre', {
  padding: '1em',
  margin: '0.5em 0',
  backgroundColor: '$gs12',
  color: '$gs1',
  borderRadius: '$3',
  overflowX: 'auto',
})

const MarkdownStyled = styled(Markdown, {
  // markdown container styling
  marginBottom: 0,
  '& > p': {
    marginBottom: '0.5em',
  },
  '& > *:last-child': {
    marginBottom: '0px',
  },
  '& > *:first-child': {
    paddingTop: '0px',
  },
  a: {
    display: 'inline',
  },
  ':is(h1, h2, h3, h4, h5, h6) + :is(h1, h2, h3, h4, h5, h6)': {
    paddingTop: '0.5em',
  },
})

// Kept for reference; logic moved to LinkWithBase inside overrides

const CustomLinkCss = css({
  textStyle: '$body1',
  fontSize: 'inherit',
  marginBottom: '$body1',
  display: 'inline-block',
  cursor: 'pointer',
  position: 'relative',
  userSelect: 'none',
  color: '$lin',
  textDecoration: 'none',
  transition: 'opacity $normal ease-in-out',
  '&:hover': {
    opacity: 0.8,
    transition: 'opacity $normal ease-in-out',
  },
})

const A = styled('a', CustomLinkCss)
const CustomLink = styled(Link, CustomLinkCss)

const list = css({
  marginTop: '0.7em',
  marginBottom: '0.7em',
  'ul, ol': {
    marginTop: '0.05em',
    marginBottom: '0.05em',
  },
  '& p': {
    marginBottom: '0px',
  },
})

const OL = styled('ol', sharedTypography, list)
const UL = styled('ul', sharedTypography, list)

const LI = styled('li', sharedTypography, {
  marginLeft: '1.35em',
  marginBottom: '0.5em',
  fontSize: '1.6rem !important',
})

const Span = styled('span', {
  fontSize: 'inherit',
  textWrap: 'pretty',
})

const P = styled('p', {
  textStyle: '$body1',
  marginBottom: '$body1',
  fontSize: '1.6rem',
  textWrap: 'pretty',
})
const H1 = styled('h1', {
  textStyle: '$h1',
  paddingTop: '1em',
  paddingBottom: '0.5em',
  fontSize: '2.8rem',
  textWrap: 'pretty',
})
const H2 = styled('h2', {
  textStyle: '$h2',
  paddingTop: '1em',
  paddingBottom: '0.5em',
  fontSize: '2.2rem',
  textWrap: 'pretty',
})
const H3 = styled('h3', {
  textStyle: '$h3',
  paddingTop: '1em',
  paddingBottom: '0.5em',
  fontSize: '1.8rem',
  textWrap: 'pretty',
})
const H4 = styled('h4', {
  textStyle: '$h4',
  paddingTop: '1em',
  paddingBottom: '0.5em',
  fontSize: '1.6rem',
  textWrap: 'pretty',
})
const H5 = styled('h5', {
  textStyle: '$h5',
  paddingTop: '1em',
  paddingBottom: '0.5em',
  fontSize: '1.4rem',
  textWrap: 'pretty',
})
const H6 = styled('h6', {
  textStyle: '$h6',
  paddingTop: '1em',
  paddingBottom: '0.5em',
  fontSize: '1.2rem',
  textWrap: 'pretty',
})
const Blockquote = styled('blockquote', {
  textStyle: '$blockquote',
  marginBottom: '$blockquote',
  fontSize: '1.4rem',
  textWrap: 'pretty',
})
